import React, { useState, useEffect } from 'react';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { outreachApi } from '../lib/api';
import { formatRelativeTime, cn } from '../lib/utils';
import { Plus, Mail, Send, Edit3, Trash2, Loader2 } from 'lucide-react';

const OutreachPage = () => {
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState([]);
  const [threads, setThreads] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newDraft, setNewDraft] = useState({
    recipient_name: '', recipient_email: '', recipient_type: 'founder',
    recipient_company: '', tone: 'professional'
  });
  const [creating, setCreating] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [editingBody, setEditingBody] = useState('');

  const recipientTypes = ['founder', 'investor', 'operator'];
  const tones = [
    { value: 'professional', label: 'Professional' },
    { value: 'concise', label: 'Concise' },
    { value: 'warm', label: 'Warm' },
    { value: 'high_conviction', label: 'High Conviction' },
    { value: 'thesis_led', label: 'Thesis-Led' }
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      const [draftsRes, threadsRes] = await Promise.all([
        outreachApi.listDrafts(),
        outreachApi.listThreads()
      ]);
      setDrafts(draftsRes.data.drafts || []);
      setThreads(threadsRes.data.threads || []);
    } catch (err) {
      console.error('Failed to fetch outreach:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await outreachApi.createDraft(newDraft);
      setDrafts([response.data, ...drafts]);
      setShowCreateModal(false);
      setNewDraft({ recipient_name: '', recipient_email: '', recipient_type: 'founder', recipient_company: '', tone: 'professional' });
      setSelectedDraft(response.data);
      setEditingBody(response.data.body);
    } catch (err) {
      console.error('Failed to create draft:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateDraft = async () => {
    if (!selectedDraft) return;
    try {
      await outreachApi.updateDraft(selectedDraft.draft_id, { body: editingBody });
      setDrafts(drafts.map(d => d.draft_id === selectedDraft.draft_id ? { ...d, body: editingBody } : d));
      setSelectedDraft({ ...selectedDraft, body: editingBody });
    } catch (err) {
      console.error('Failed to update draft:', err);
    }
  };

  const handleDeleteDraft = async (draftId) => {
    if (!window.confirm('Delete this draft?')) return;
    try {
      await outreachApi.deleteDraft(draftId);
      setDrafts(drafts.filter(d => d.draft_id !== draftId));
      if (selectedDraft?.draft_id === draftId) {
        setSelectedDraft(null);
      }
    } catch (err) {
      console.error('Failed to delete draft:', err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'text-emerald-400 bg-emerald-400/10';
      case 'replied': return 'text-blue-400 bg-blue-400/10';
      case 'draft': return 'text-amber-400 bg-amber-400/10';
      default: return 'text-zinc-400 bg-zinc-400/10';
    }
  };

  return (
    <MainLayout>
      <div className="p-6 h-screen flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-light font-['Outfit'] text-zinc-50">Outreach</h1>
            <p className="text-zinc-400 font-['Manrope']">Draft and manage your outreach emails</p>
          </div>
          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button data-testid="create-outreach-btn" className="bg-white text-zinc-900 hover:bg-zinc-200">
                <Plus className="w-4 h-4 mr-2" />
                New Outreach
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-zinc-50 font-['Outfit']">Draft New Outreach</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 block">Recipient Name</label>
                    <Input
                      value={newDraft.recipient_name}
                      onChange={(e) => setNewDraft({ ...newDraft, recipient_name: e.target.value })}
                      placeholder="John Doe"
                      required
                      data-testid="input-recipient-name"
                      className="bg-zinc-800 border-zinc-700 text-zinc-50"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 block">Email</label>
                    <Input
                      value={newDraft.recipient_email}
                      onChange={(e) => setNewDraft({ ...newDraft, recipient_email: e.target.value })}
                      placeholder="john@company.com"
                      type="email"
                      data-testid="input-recipient-email"
                      className="bg-zinc-800 border-zinc-700 text-zinc-50"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 block">Type</label>
                    <Select 
                      value={newDraft.recipient_type} 
                      onValueChange={(v) => setNewDraft({ ...newDraft, recipient_type: v })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        {recipientTypes.map((t) => (
                          <SelectItem key={t} value={t} className="text-zinc-50 capitalize">{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 block">Company</label>
                    <Input
                      value={newDraft.recipient_company}
                      onChange={(e) => setNewDraft({ ...newDraft, recipient_company: e.target.value })}
                      placeholder="Acme Inc"
                      data-testid="input-recipient-company"
                      className="bg-zinc-800 border-zinc-700 text-zinc-50"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Tone</label>
                  <Select 
                    value={newDraft.tone} 
                    onValueChange={(v) => setNewDraft({ ...newDraft, tone: v })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {tones.map((t) => (
                        <SelectItem key={t.value} value={t.value} className="text-zinc-50">{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} className="border-zinc-700 text-zinc-400">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={creating || !newDraft.recipient_name} data-testid="submit-outreach-btn" className="bg-white text-zinc-900 hover:bg-zinc-200">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {creating ? 'Generating...' : 'Generate Draft'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Content */}
        <Tabs defaultValue="drafts" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="bg-zinc-900 border border-zinc-800 mb-6 w-fit">
            <TabsTrigger value="drafts" className="data-[state=active]:bg-zinc-800">
              Drafts ({drafts.length})
            </TabsTrigger>
            <TabsTrigger value="threads" className="data-[state=active]:bg-zinc-800">
              Threads ({threads.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="drafts" className="flex-1 flex gap-6 overflow-hidden m-0">
            {/* Drafts List */}
            <div className="w-80 flex-shrink-0 overflow-y-auto">
              {loading ? (
                <div className="space-y-3">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-md p-4 animate-pulse">
                      <div className="h-4 w-24 bg-zinc-800 rounded mb-2" />
                      <div className="h-3 w-32 bg-zinc-800 rounded" />
                    </div>
                  ))}
                </div>
              ) : drafts.length > 0 ? (
                <div className="space-y-2">
                  {drafts.map((draft) => (
                    <div
                      key={draft.draft_id}
                      onClick={() => { setSelectedDraft(draft); setEditingBody(draft.body); }}
                      data-testid={`draft-card-${draft.draft_id}`}
                      className={cn(
                        "bg-zinc-900 border rounded-md p-4 cursor-pointer transition-colors",
                        selectedDraft?.draft_id === draft.draft_id ? "border-zinc-600" : "border-zinc-800 hover:border-zinc-700"
                      )}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-zinc-50 font-medium font-['Outfit'] truncate">{draft.recipient_name}</h3>
                        <span className={cn("px-2 py-0.5 text-xs font-mono rounded-full", getStatusColor(draft.status))}>
                          {draft.status}
                        </span>
                      </div>
                      <p className="text-zinc-400 text-sm font-['Manrope'] truncate">{draft.recipient_company || draft.recipient_type}</p>
                      <p className="text-zinc-500 text-xs font-mono mt-2">{formatRelativeTime(draft.created_at)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-zinc-900 border border-zinc-800 rounded-md p-8 text-center">
                  <Mail className="w-10 h-10 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400 font-['Manrope'] mb-4">No drafts yet</p>
                  <Button onClick={() => setShowCreateModal(true)} className="bg-white text-zinc-900 hover:bg-zinc-200">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Draft
                  </Button>
                </div>
              )}
            </div>

            {/* Draft Editor */}
            <div className="flex-1 overflow-y-auto">
              {selectedDraft ? (
                <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="text-lg font-medium font-['Outfit'] text-zinc-50">To: {selectedDraft.recipient_name}</h2>
                      <p className="text-zinc-400 text-sm">{selectedDraft.recipient_email || 'No email'} • {selectedDraft.recipient_company}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" onClick={() => handleDeleteDraft(selectedDraft.draft_id)} className="text-zinc-500 hover:text-rose-400">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="text-sm text-zinc-400 mb-2 block">Subject</label>
                    <Input
                      value={selectedDraft.subject}
                      readOnly
                      className="bg-zinc-800 border-zinc-700 text-zinc-50"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="text-sm text-zinc-400 mb-2 block">Body</label>
                    <textarea
                      value={editingBody}
                      onChange={(e) => setEditingBody(e.target.value)}
                      rows={12}
                      data-testid="draft-body-editor"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-zinc-50 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-zinc-600"
                    />
                  </div>

                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={handleUpdateDraft} className="border-zinc-700 text-zinc-400">
                      <Edit3 className="w-4 h-4 mr-2" />
                      Save Changes
                    </Button>
                    <Button className="bg-white text-zinc-900 hover:bg-zinc-200">
                      <Send className="w-4 h-4 mr-2" />
                      Send (Gmail not connected)
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <Mail className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                    <p className="text-zinc-500 font-['Manrope']">Select a draft to edit</p>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="threads" className="flex-1 overflow-y-auto m-0">
            {threads.length > 0 ? (
              <div className="space-y-3">
                {threads.map((thread) => (
                  <div
                    key={thread.thread_id}
                    data-testid={`thread-card-${thread.thread_id}`}
                    className="bg-zinc-900 border border-zinc-800 rounded-md p-4 cursor-pointer hover:border-zinc-700"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-zinc-50 font-medium font-['Outfit']">{thread.recipient_name}</h3>
                        <p className="text-zinc-400 text-sm">{thread.recipient_email}</p>
                      </div>
                      <span className={cn("px-2 py-0.5 text-xs font-mono rounded-full", getStatusColor(thread.status))}>
                        {thread.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-zinc-900 border border-zinc-800 rounded-md p-12 text-center">
                <Mail className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400 font-['Manrope']">No threads yet</p>
                <p className="text-zinc-500 text-sm mt-2">Sent emails will appear here</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default OutreachPage;
